<script setup lang="ts">
import { ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { useCustomToast } from '@/shared/lib'

interface Props {
  text: string
  displayText?: string
  truncate?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  truncate: true,
})

const { t } = useI18n()
const { success } = useCustomToast()

const copied = ref(false)
let copyTimeout: number | null = null

async function handleCopy() {
  if (!props.text) 
    return

  try {
    await navigator.clipboard.writeText(props.text)
    copied.value = true
    success({ title: t('copied'), message: '' })

    if (copyTimeout) 
      clearTimeout(copyTimeout)

    copyTimeout = window.setTimeout(() => {
      copied.value = false
    }, 2000)
  }
  catch (err) {
    console.error('Failed to copy:', err)
  }
}
</script>

<template>
  <button
    type="button"
    class="copyable-text group inline-flex items-center gap-1.5 max-w-full text-left transition-colors hover:text-primary focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/50 rounded"
    :title="text"
    :aria-label="t('click_to_copy')"
    @click="handleCopy"
  >
    <span class="font-mono" :class="{ truncate }">
      {{ displayText || text }}
    </span>
    <span
      class="flex-shrink-0 opacity-0 group-hover:opacity-100 transition-opacity"
      :class="{ '!opacity-100': copied }"
    >
      <svg
        v-if="copied"
        class="w-4 h-4 text-[#67C23A]"
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
      >
        <path
          stroke-linecap="round"
          stroke-linejoin="round"
          stroke-width="2.5"
          d="M5 13l4 4L19 7"
        />
      </svg>
      <svg
        v-else
        class="w-4 h-4 text-text-regular"
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
      >
        <path
          stroke-linecap="round"
          stroke-linejoin="round"
          stroke-width="2"
          d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"
        />
      </svg>
    </span>
  </button>
</template>

<style scoped>
.copyable-text {
  cursor: pointer;
}
</style>
